import {
  forwardRef,
  type ComponentPropsWithoutRef,
  createContext,
  useContext,
} from "react"
import { cn } from "@/shared/utils/general/cn"

/**
 * Radio / RadioGroup — matches v1 MUI Radio styling.
 *
 * v1 styles ported:
 *   - 24px icon size
 *   - Default: foreground @ 30% opacity
 *   - Hover: foreground @ 50%, primary bg @ 4%
 *   - Checked: primary color fill
 *   - Disabled: foreground @ 26% opacity
 */

// --- Context for RadioGroup value ---

interface RadioGroupContextValue {
  value?: string
  onValueChange?: (value: string) => void
  name?: string
}

const RadioGroupContext = createContext<RadioGroupContextValue>({})

// --- RadioGroup ---

export interface RadioGroupProps extends ComponentPropsWithoutRef<"div"> {
  value?: string
  onValueChange?: (value: string) => void
  name?: string
}

const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(
  ({ className, value, onValueChange, name, ...props }, ref) => {
    return (
      <RadioGroupContext.Provider value={{ value, onValueChange, name }}>
        <div
          ref={ref}
          role="radiogroup"
          className={cn("flex flex-col gap-1", className)}
          {...props}
        />
      </RadioGroupContext.Provider>
    )
  },
)
RadioGroup.displayName = "RadioGroup"

// --- RadioGroupItem ---

export interface RadioGroupItemProps extends Omit<
  ComponentPropsWithoutRef<"button">,
  "value"
> {
  value: string
}

const RadioGroupItem = forwardRef<HTMLButtonElement, RadioGroupItemProps>(
  ({ className, value, disabled, ...props }, ref) => {
    const ctx = useContext(RadioGroupContext)
    const checked = ctx.value === value

    return (
      <button
        ref={ref}
        role="radio"
        type="button"
        aria-checked={checked}
        disabled={disabled}
        data-state={checked ? "checked" : "unchecked"}
        className={cn(
          "inline-flex size-6 shrink-0 items-center justify-center rounded-full border-2 p-0 transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-2",
          checked
            ? "border-primary"
            : "border-foreground/30 hover:border-foreground/50 hover:bg-primary/4",
          disabled && "cursor-not-allowed opacity-26",
          className,
        )}
        onClick={() => {
          if (!disabled) ctx.onValueChange?.(value)
        }}
        {...props}
      >
        {checked && <span className="size-3 rounded-full bg-primary" />}
        {/* Hidden input for form submission */}
        <input
          type="radio"
          name={ctx.name}
          value={value}
          checked={checked}
          readOnly
          className="sr-only"
        />
      </button>
    )
  },
)
RadioGroupItem.displayName = "RadioGroupItem"

export { RadioGroup, RadioGroupItem }
